/**
 * This component handles global error states in the application.
 * It displays error messages and provides feedback to users when something goes wrong.
 */
"use client";

import Link from "next/link";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="max-w-lg w-full text-center">
            <div className="mb-8">
              <svg
                className="mx-auto h-20 w-20 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">
              Application Error
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Something went wrong with the application. This is a critical
              error that requires immediate attention.
            </p>
            <div className="space-y-4">
              <button
                onClick={reset}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-primary text-primary-foreground py-3 px-6 rounded hover:bg-primary/90 transition-colors font-medium"
              >
                Reload Application
              </button>
              <Link
                href="/"
                className="inline-block w-full bg-secondary text-secondary-foreground py-3 px-6 rounded hover:bg-secondary/80 transition-colors font-medium"
              >
                Return to Home
              </Link>
            </div>
            {process.env.NODE_ENV === "development" && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 mb-2">
                  Error Details (Development Only)
                </summary>
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <pre className="text-xs text-red-700 overflow-auto">
                    <strong>Error:</strong> {error.message}
                    {error.digest && `\n\nDigest: ${error.digest}`}
                    {error.stack && `\n\nStack Trace:\n${error.stack}`}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
